import { forwardRef, useMemo, useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';

import Dropdown from '@webclient/components/invitation-form/components/dropdown';
import FieldSetWrapper from '@webclient/components/invitation-form/components/FieldSetWrapper';
import CONFIG from '@webclient/components/invitation-form/config';
import {
  StyledStackButtons,
  StyledStackFields,
} from '@webclient/components/invitation-form/styles';
import { notification } from '@webclient/components/notification';
import FormProvider from '@webclient/components/react-hook-form/FormProvider';
import RHFInputField from '@webclient/components/react-hook-form/RHFInputField';
import useWorkspaceSetup from '@webclient/components/workspace/setup/hooks/useWorkspaceSetup';

import useCreateInvitation from '@webclient/hooks/useCreateInvitation';

import DeleteIcon from '~icons/knowz-iconify/delete-rounded';
import HelpRoundedIcon from '~icons/knowz-iconify/help-rounded';
import CheckMailIcon from '~icons/knowz-iconify/mail-check-rounded';
import MailIcon from '~icons/knowz-iconify/mail-rounded';
import PlusIcon from '~icons/knowz-iconify/plus';

import { faker } from '@faker-js/faker';
import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { isAxiosError } from 'axios';
import PQueue from 'p-queue';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

const defaultValues = {
  membersToInvite: [
    {
      email: '',
      role: CONFIG.DEFAULT_VALUE,
    },
  ],
};

const pQ = new PQueue({ concurrency: 100 });

type Props = {
  onSubmitHandler?: VoidFunction;
  onSuccessHandler?: VoidFunction;
};

const InvitationForm = forwardRef<HTMLDivElement, Props>(
  ({ onSubmitHandler, onSuccessHandler }, ref) => {
    const { t } = useTranslate();
    const theme = useTheme();
    const [formState, setFormState] = useState<
      'submitting' | 'submitted' | 'disabled' | null
    >(null);

    const {
      handleSetMemberToInvite,
      handleUpdateMember,
      membersToInvite,
      handleDeleteMember,
    } = useWorkspaceSetup();
    const methods = useForm({
      defaultValues:
        membersToInvite.length > 0
          ? {
              membersToInvite: [
                ...membersToInvite,
                {
                  email: '',
                  role: CONFIG.DEFAULT_VALUE,
                },
              ],
            }
          : defaultValues,
    });
    const { createInvitationAsync } = useCreateInvitation({
      onError: (error, variables) => {
        if (isAxiosError(error)) {
          const data = error.response?.data as {
            message: string;
            code: number;
          };
          if (
            data.code === 400 &&
            data.message.toLowerCase().includes('membership in header')
          ) {
            return notification.error({
              message: t(
                'page.workspace.createWorkspaceModal.step3.form.email.workspace',
                'You need to be in a workspace to invite members',
              ),
            });
          }

          if (
            data.code === 402 &&
            data.message.toLowerCase().includes('reached the limit')
          ) {
            return notification.error({
              message: t(
                'page.workspace.createWorkspaceModal.step3.form.email.reachLimit',
                'You have reached the limit of members you can invite',
              ),
            });
          }
        }
        notification.error({
          message: t(
            'page.workspace.createWorkspaceModal.step3.form.toastMessages.failure',
            'Invitation failed to send to {email}, Please check the email address and try again.',
            {
              email: variables.email,
            },
          ),
        });
      },
      onSuccess: (data) => {
        onSuccessHandler?.();
        notification.success({
          message: t(
            'page.workspace.createWorkspaceModal.step3.form.toastMessages.success',
            'Invitation has been sent successfully to {email}',
            {
              email: data.emailInvite,
            },
          ),
        });
      },
    });
    const {
      handleSubmit,
      formState: { isDirty, errors },
      control,
      register,
      watch,
      setError,
      clearErrors,
    } = methods;

    const { fields, append, remove } = useFieldArray({
      name: 'membersToInvite',
      control,
    });
    const stillNeedToSendInvitation = useMemo(
      () => membersToInvite.some((member) => !member.isInvitationSent),
      [membersToInvite],
    );

    const shouldDisableSendInvitesButton =
      (stillNeedToSendInvitation && !isDirty) ||
      (!stillNeedToSendInvitation &&
        watch('membersToInvite').at(-1)?.email === '') ||
      formState === 'submitting';

    async function onSubmit() {
      if ('membersToInvite' in errors) {
        return notification.error({
          message: 'Please make sure email addresses are valid.',
        });
      }

      setFormState('submitting');
      const invites = [...membersToInvite];
      const latestFilledField = watch('membersToInvite').at(-1)!;
      if (latestFilledField.email) {
        const fakeRandomId = faker.string.uuid();
        handleSetMemberToInvite({
          ...latestFilledField,
          id: fakeRandomId,
          isInvitationSent: false,
        });
        invites.push({
          ...latestFilledField,
          id: fakeRandomId,
          isInvitationSent: false,
        });
        append({
          email: '',
          role: CONFIG.DEFAULT_VALUE,
        });
      }

      invites.forEach((member, index) => {
        pQ.add(async () => {
          if (member.isInvitationSent) {
            return;
          }
          return await createInvitationAsync({
            email: member.email,
            role: member.role,
          })
            .catch((error) => {
              console.error(error);
              handleDeleteMember(member.email);
              remove(index);
            })
            .finally(() => {
              handleUpdateMember({
                ...member,
                isInvitationSent: true,
              });
              onSubmitHandler && onSubmitHandler();
            });
        });
      });

      pQ.on('idle', () => {
        setFormState('submitted');
      });
    }

    function handleAddAnotherSetOfFields() {
      append({
        email: '',
        role: CONFIG.DEFAULT_VALUE,
      });
      const latestFilledField = watch('membersToInvite').at(-2)!;
      handleSetMemberToInvite({
        ...latestFilledField,
        id: faker.string.uuid(),
        isInvitationSent: false,
      });
    }

    return (
      <Container
        ref={ref}
        disableGutters
      >
        <FormProvider
          onSubmit={handleSubmit(handleAddAnotherSetOfFields)}
          methods={methods}
        >
          <Stack sx={{ alignItems: 'flex-start' }}>
            <Stack
              direction="row"
              gap={1}
              sx={{ alignItems: 'center' }}
            >
              <Text
                variant="textMd"
                weight="semibold"
              >
                {t(
                  'page.workspace.createWorkspaceModal.step3.form.title',
                  'Invite team members',
                )}
              </Text>
              <Box>
                <HelpRoundedIcon
                  color={theme.palette.foreground.quaternary}
                  fontSize="0.8em"
                />
              </Box>
            </Stack>
            <Text
              variant="textSm"
              color="text.tertiary"
            >
              {t(
                'page.workspace.createWorkspaceModal.step3.form.description',
                'Get your projects up and running faster by inviting your team to collaborate.',
              )}
            </Text>
          </Stack>
          <StyledStackFields gap={3}>
            {fields.map((item, index, originalArray) => (
              <FieldSetWrapper key={item.id}>
                <RHFInputField
                  {...register(`membersToInvite.${index}.email`, {
                    onChange: (e) => {
                      const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                        e.target.value,
                      );
                      if (!validEmail) {
                        setError(`membersToInvite.${index}.email`, {
                          type: 'manual',
                          message: t(
                            'page.workspace.createWorkspaceModal.step3.form.email.invalidEmail',
                            'Please enter a valid email address',
                          ),
                        });
                      } else {
                        clearErrors(`membersToInvite.${index}.email`);
                      }
                    },
                  })}
                  fullWidth
                  type="text"
                  disabled={
                    originalArray.length - 1 !== index &&
                    (membersToInvite[index]?.isInvitationSent ?? false)
                  }
                  startAdornment={<MailIcon />}
                  placeholder={t(
                    'page.workspace.createWorkspaceModal.step3.form.email.placeholder',
                    'Ex: member@gmail.com',
                  )}
                />
                <Controller
                  {...register(`membersToInvite.${index}.role`)}
                  render={({ field: { onChange } }) => {
                    return (
                      <Dropdown
                        disabled={
                          originalArray.length - 1 !== index &&
                          (membersToInvite[index]?.isInvitationSent ?? false)
                        }
                        role={CONFIG.DEFAULT_VALUE}
                        roleItems={CONFIG.ROLE_ITEMS}
                        onChange={(role) => {
                          onChange(role);
                        }}
                      />
                    );
                  }}
                />

                {originalArray.length - 1 !== index &&
                  ((membersToInvite[index]?.isInvitationSent ?? false) ? (
                    <IconButton
                      color="secondary"
                      variant="outlined"
                      disabled
                    >
                      <CheckMailIcon
                        fontSize="1.3em"
                        color={theme.palette.foreground.secondary}
                      />
                    </IconButton>
                  ) : (
                    <IconButton
                      color="secondary"
                      variant="outlined"
                      onClick={() => remove(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  ))}
              </FieldSetWrapper>
            ))}
          </StyledStackFields>
          <StyledStackButtons direction="row">
            <ButtonBase
              sx={{
                ':disabled': {
                  color: theme.palette.foreground.disabled,
                },
              }}
              disableRipple
              disableTouchRipple
              onClick={handleAddAnotherSetOfFields}
            >
              <PlusIcon />
              {t(
                'page.workspace.createWorkspaceModal.addAnother',
                'Add another',
              )}
            </ButtonBase>
            <LoadingButton
              size="small"
              disabled={shouldDisableSendInvitesButton}
              variant="contained"
              onClick={onSubmit}
              startIcon={<MailIcon />}
            >
              {t(
                'page.workspace.createWorkspaceModal.step3.form.button.sendInvites',
                'Send Invites',
              )}
            </LoadingButton>
          </StyledStackButtons>
        </FormProvider>
      </Container>
    );
  },
);

export default InvitationForm;
